/**
 * Identity Verification.
 *
 * Salvadoran DUI uses mandatory front/back photos as retained evidence plus
 * the official lookup path for the actual identity decision.
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { ActivityIndicator, Linking, View, Text } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Image as ExpoImage } from 'expo-image'
import Animated, { FadeInDown, FadeOut, useReducedMotion } from 'react-native-reanimated'
import { Camera, CheckCircle, AlertCircle, RotateCcw, Upload, Clock } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useIsDesktopWeb } from '@/lib/hooks/use-is-desktop-web'
import { isExpectedNonFatalOperationalError } from '@/lib/errors/service-error'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { Select, type SelectOption } from '@/components/ui/Select'
import {
  DEFAULT_IDENTITY_COUNTRY_ISO2,
  IDENTITY_REGISTRY_COUNTRIES,
  identityRegistryFor,
} from '@/lib/data/identity-registries'
import { findCountry } from '@/lib/data/countries'
import { Pressable } from '@/components/ui/Pressable'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { ExtractedFieldsCard } from '@/components/verification/ExtractedFieldsCard'
import { useIdentityVerification } from '@/lib/hooks/use-identity-verification'
import { useAuth } from '@/lib/hooks/use-auth'
import { useVerificationOverview } from '@/lib/hooks/use-verification'
import { useSemanticColors, SPACING, RADIUS, DURATION } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { verificationService } from '@/lib/services/verification.service'
import type { IdDocumentType } from '@/lib/types/verification.types'

export default function IdentityScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const reducedMotion = useReducedMotion()
  const isDesktopWeb = useIsDesktopWeb()
  const { data: overview, isLoading: overviewLoading } = useVerificationOverview()

  const [documentType, setDocumentType] = useState<IdDocumentType>('dui')
  /**
   * De qué país es el documento.
   *
   * Estaba escrito `'SV'` en la configuración del flujo, y ese literal era toda
   * la restricción geográfica del producto: no había municipio, ni lista de
   * ciudades, ni geocerca — había un país que nadie podía cambiar porque no se
   * preguntaba. Ahora se pregunta, y a qué registro va cada respuesta lo dice
   * `lib/data/identity-registries.ts`.
   */
  const [issuingCountry, setIssuingCountry] = useState<string>(DEFAULT_IDENTITY_COUNTRY_ISO2)
  /** Provincia o estado. Solo existe donde el registro es sub-nacional. */
  const [issuingRegion, setIssuingRegion] = useState('')
  const registry = identityRegistryFor(issuingCountry)
  const identityStatus = overview?.identity.status
  const identityApproved = identityStatus === 'approved'

  const flow = useIdentityVerification({
    userId: user?.id ?? null,
    documentType,
    issuingCountry,
    issuingRegion: issuingRegion.trim() || undefined,
    requireBack: documentType !== 'passport',
    // El documento nacional con registro se LEE primero y se consulta DESPUÉS:
    // el OCR extrae número y fecha, la persona los ve y corrige si hace falta,
    // y recién ahí se gasta la consulta — un dígito mal leído devuelve "no
    // existe" y cuesta lo mismo que una consulta buena. El pasaporte no tiene
    // registro, así que se lee y se envía directo.
    autoSubmit: true,
    extractOnly: documentType !== 'passport' && registry !== null,
  })

  const {
    state,
    isConfirming,
    captureFront,
    captureBack,
    retakeFront,
    retakeBack,
    updateField,
    confirmAndPersist,
    resume,
    hydrate,
    reset,
  } = flow
  const waitingForPersistedIdentity = overviewLoading && state.phase === 'idle'

  /**
   * Una verificación que ya está en la cola manual (sin registro que
   * consultar): se muestra el aviso de revisión y nada de captura.
   */
  const [mountedInReview, setMountedInReview] = useState(false)
  const hydrationRan = useRef(false)

  // ─── Fase 0 (contención): al ABRIR identidad, leer la fila pendiente ─────
  //
  // El incidente de iOS: el teléfono cortaba a los 15 segundos mientras el
  // edge seguía leyendo el DUI, la persona volvía a entrar y empezaba de cero
  // — pagando dos veces y perdiendo una extracción que ya estaba guardada.
  // Ahora, al montar, se busca el envío pendiente: si el OCR ya leyó algo, los
  // campos aparecen SIN recaptura; si hay fotos sin leer, se ofrece retomar
  // con la MISMA evidencia; si está en revisión manual, se muestra eso.
  useEffect(() => {
    if (
      hydrationRan.current ||
      !user?.id ||
      overviewLoading ||
      identityApproved ||
      state.phase !== 'idle'
    ) {
      return
    }
    hydrationRan.current = true
    let cancelled = false
    void (async () => {
      try {
        const pending = await verificationService.getPendingIdentitySubmission(user.id)
        if (cancelled || !pending) return
        if (pending.providerStatus === 'active_unverified') {
          router.replace('/profile/verification/selfie')
          return
        }
        const canonical = pending.canonicalStatus
        const inManualReview = canonical === 'review' || canonical === 'in_progress'
        if (inManualReview && identityRegistryFor(pending.issuingCountry) === null) {
          setMountedInReview(true)
          return
        }
        // La fila viene de una captura de OTRO país/tipo: la pantalla adopta
        // su contexto para que confirmar reúse exactamente esa fila.
        if (pending.documentType) setDocumentType(pending.documentType)
        if (pending.issuingCountry) setIssuingCountry(pending.issuingCountry)
        if (pending.issuingRegion) setIssuingRegion(pending.issuingRegion)
        if (pending.hasExtraction) {
          hydrate(
            {
              recordId: pending.recordId,
              extracted: pending.extracted,
              canonicalStatus: canonical,
            },
            'extracted',
          )
          return
        }
        hydrate(
          {
            recordId: pending.recordId,
            extracted: {},
            canonicalStatus: canonical,
          },
          'resume',
        )
      } catch (err) {
        captureError(err, { screen: 'verification/identity', action: 'mount-resume' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [hydrate, identityApproved, overviewLoading, router, state.phase, user?.id])

  // When the flow terminates in `confirmed` — and ONLY with an approval —
  // bounce back to the overview. A review/pending confirmation stays visible:
  // navigating away on a "quedó en revisión" notice is how a person is left
  // believing they were approved.
  useEffect(() => {
    if (state.nextStep === 'selfie') router.replace('/profile/verification/selfie')
  }, [router, state.nextStep])

  useEffect(() => {
    if (state.phase === 'confirmed' && state.canonicalStatus === 'approved') {
      const timer = setTimeout(() => router.back(), 1200)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [state.phase, state.canonicalStatus, router])

  // El documento "nacional" se llama distinto en cada país, y llamarlo DUI en
  // México no es un detalle de traducción: es pedirle a alguien un papel que no
  // tiene. La opción es la misma —`dui` es el valor del enum para "documento
  // nacional de identidad"— y la etiqueta la pone el registro del país.
  /**
   * Cómo se llama el documento ACÁ. Toda la pantalla lo interpola en vez de
   * decir "DUI": el país lo elige la persona, y pedirle un DUI a alguien en
   * México no es un desliz de traducción — es pedirle un papel que no existe.
   */
  const documentLabel = t(registry?.documentLabelKey ?? 'verification.document_type_national_id')

  const documentTypeOptions: SelectOption<IdDocumentType>[] = [
    {
      value: 'dui',
      label: documentLabel,
    },
    { value: 'passport', label: t('verification.document_type_passport') },
  ]

  // Solo las jurisdicciones que el registro atiende. Un campo libre acá deja
  // teclear "California" y llegar hasta después de subir las dos fotos para
  // enterarse de que no se puede confirmar.
  const regionOptions: SelectOption<string>[] = (registry?.supportedRegions ?? []).map((r) => ({
    value: r.value,
    label: r.label,
  }))

  const countryOptions: SelectOption<string>[] = IDENTITY_REGISTRY_COUNTRIES.map((entry) => ({
    value: entry.iso2,
    label:
      findCountry(entry.iso2)?.[
        i18n.language?.toLowerCase().startsWith('en') ? 'nameEn' : 'nameEs'
      ] ?? entry.iso2,
  }))

  const handleConfirm = useCallback(async () => {
    try {
      const result = await confirmAndPersist()
      // `confirmAndPersist` resolves to null when there is no session or no
      // persisted identity record to update — nothing reached the server.
      // This used to fall into the success branch, so a save that never
      // happened announced "¡Listo! Ya estás verificado".
      if (!result) {
        toast.error(t('verification.error_failed'))
        return
      }
      if (result.nextStep === 'selfie') {
        return
      }
      if (result.status === 'approved') {
        toast.success(t('verification.result_approved_title'))
        return
      }
      if (result.status === 'declined') {
        toast.error(result.rejectionReason ?? t('verification.result_declined_desc'))
        return
      }
      // 'review' / 'in_progress': the document IS saved and queued, so this is
      // not an error — the flow also parks in the `confirmed` phase here, and
      // a red "retry" toast on top of a green badge read as a contradiction.
      if (result.status === 'review' || result.status === 'in_progress') {
        toast.info(t('verification.identity_in_review_notice'))
        return
      }
      // 'pending' / 'expired' — the flow lands in the `error` phase.
      toast.error(t('verification.verifik_retry_required'))
    } catch (err) {
      // Surface the real reason and capture it. Identity confirm
      // failures (RLS denial on identity_documents UPDATE, partial
      // unique constraint conflict, edge fn timeout) were silent
      // before — only a generic toast with no Sentry signal.
      if (__DEV__) console.error('[Identity confirm]', err)
      toast.error(errorMessage(err, t('verification.error_failed')))
      // Un 4xx de entrada (VALIDATION_ERROR) es el resultado esperado que el
      // toast ya explica; reportarlo convertía un error de tipeo del usuario en
      // una alerta de producción (TAGO-MOBILE-57). Los defectos reales —5xx,
      // red, RLS— siguen llegando a Sentry.
      if (!isExpectedNonFatalOperationalError(err)) {
        captureError(err, { screen: 'verification/identity', action: 'confirm' })
      }
    }
  }, [confirmAndPersist, t])

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('verification.identity_title')} backTestID="identity.back" />

      <KeyboardAwareView includeTopInset={false} bottomOffset={SPACING.xl * 2}>
        {/* KeyboardAwareView pads its content SPACING.md from the top, on top
            of the SPACING.md ScreenHeader already leaves under the title — 32pt
            of air here against the 16pt the two sibling steps of this flow
            (licencia, declaración) start their copy at. Stepping through the
            three screens made the copy jump. Cancelling the duplicate lands
            them at the same height. */}
        <View style={{ marginTop: -SPACING.md, gap: SPACING.lg }}>
          {/* Intro */}
          <Text
            style={{
              fontSize: 14,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              lineHeight: 20,
            }}
          >
            {isDesktopWeb
              ? documentType === 'dui'
                ? t('verification.dui_lookup_intro_web', { document: documentLabel })
                : t('verification.photo_first_intro_web')
              : documentType === 'dui'
                ? t('verification.dui_lookup_intro', { document: documentLabel })
                : t('verification.photo_first_intro')}
          </Text>

          {waitingForPersistedIdentity ? (
            <View
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                paddingVertical: SPACING.xl,
              }}
            >
              {/* `textBrand`, not the raw brand purple: #480C82 on the dark
                  page is ~1.5:1, so the only thing on screen while the record
                  loads was an invisible spinner. */}
              <ActivityIndicator
                color={colors.textBrand}
                accessibilityLabel={t('common.loading')}
              />
            </View>
          ) : identityApproved ? (
            <>
              <ApprovedBadge desc={t('verification.result_approved_desc')} />
              <Button
                title={t('common.done')}
                onPress={() => router.back()}
                variant="primary"
                size="lg"
                testID="verification.identity-approved-done"
              />
            </>
          ) : mountedInReview ? (
            <>
              {/* Reloj/escudo neutral, NUNCA check verde: "en revisión" no es
                  "aprobado", y este estado lo encontró la pantalla al abrir,
                  no lo acaba de pedir la persona. */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: SPACING.md,
                  backgroundColor: `${colors.warning}14`,
                  padding: SPACING.md,
                  borderRadius: RADIUS.lg,
                }}
              >
                <Clock size={22} color={colors.warning} />
                <Text
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                    lineHeight: 20,
                  }}
                >
                  {t('verification.identity_in_review_notice')}
                </Text>
              </View>
              <Button
                title={t('common.done')}
                onPress={() => router.back()}
                variant="primary"
                size="lg"
                testID="verification.identity-review-done"
              />
            </>
          ) : (
            <>
              {/* Document type selector — only shows when idle */}
              {state.phase === 'idle' && (
                <Animated.View
                  entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
                  collapsable={false}
                  // Select already reserves FORM_FIELD_GAP (32) below itself,
                  // and the column adds its own SPACING.lg on top — 56pt of
                  // air before the card, against 17/25 everywhere above it.
                  // Cancelling the column gap lands the card on the 32pt
                  // section step.
                  style={{ marginBottom: -SPACING.lg }}
                >
                  <Select
                    label={t('verification.issuing_country')}
                    value={issuingCountry}
                    options={countryOptions}
                    onChange={(value) => {
                      setIssuingCountry(value)
                      // La provincia pertenece al país anterior. Arrastrarla es
                      // cómo se consulta el registro de Ontario con una licencia
                      // de California.
                      setIssuingRegion('')
                      reset()
                    }}
                    placeholder={t('verification.issuing_country')}
                    testID="verification.issuing-country"
                  />
                  <Select
                    label={t('verification.document_type')}
                    value={documentType}
                    options={documentTypeOptions}
                    onChange={(v) => setDocumentType(v)}
                    placeholder={t('verification.document_type')}
                    testID="verification.document-type"
                  />
                  {registry?.requiresRegion && documentType !== 'passport' ? (
                    <Select
                      label={t('verification.issuing_region')}
                      value={issuingRegion}
                      options={regionOptions}
                      onChange={setIssuingRegion}
                      placeholder={t('verification.issuing_region_placeholder')}
                      testID="verification.issuing-region"
                    />
                  ) : null}
                </Animated.View>
              )}

              {/* Capture state — big primary CTA */}
              {(state.phase === 'idle' || state.phase === 'scanning-front') && (
                <CapturePrompt
                  title={
                    isDesktopWeb
                      ? t('verification.upload_instruction_front')
                      : t('verification.instruction_front')
                  }
                  cta={
                    isDesktopWeb ? t('verification.upload_front') : t('verification.tap_to_start')
                  }
                  icon={isDesktopWeb ? 'upload' : 'camera'}
                  onPress={captureFront}
                  testID="verification.capture-front"
                />
              )}

              {/* Front captured, waiting on back */}
              {state.phase === 'scanning-back' && state.frontUri ? (
                <>
                  <CapturedPhotoRow
                    uri={state.frontUri}
                    label={t('verification.front_captured')}
                    onRetake={retakeFront}
                  />
                  <CapturePrompt
                    title={
                      isDesktopWeb
                        ? t('verification.upload_instruction_back')
                        : t('verification.instruction_back')
                    }
                    cta={
                      isDesktopWeb
                        ? t('verification.upload_back')
                        : t('verification.tap_to_capture_back')
                    }
                    icon={isDesktopWeb ? 'upload' : 'camera'}
                    onPress={captureBack}
                    testID="verification.capture-back"
                  />
                </>
              ) : null}

              {/* Uploading / verifying with in-flight spinner pill */}
              {(state.phase === 'uploading' || state.phase === 'verifying' || isConfirming) && (
                <VerifyingState
                  frontUri={state.frontUri}
                  backUri={state.backUri}
                  phase={state.phase === 'uploading' ? 'uploading' : 'verifying'}
                  documentType={documentType}
                  label={
                    documentType === 'dui'
                      ? t('verification.verifying', { document: documentLabel })
                      : t('verification.verifying_didit')
                  }
                />
              )}

              {/* Extracted — show editable card + confirm button */}
              {!isConfirming && (state.phase === 'extracted' || state.phase === 'confirmed') && (
                <>
                  <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                    {state.frontUri ? (
                      <ThumbPreview uri={state.frontUri} onRetake={retakeFront} />
                    ) : null}
                    {state.backUri ? (
                      <ThumbPreview uri={state.backUri} onRetake={retakeBack} />
                    ) : null}
                  </View>
                  <ExtractedFieldsCard
                    data={state.extracted}
                    onChange={updateField}
                    documentType={documentType}
                  />
                  <Button
                    title={t('verification.confirm_looks_good')}
                    onPress={handleConfirm}
                    variant="primary"
                    size="lg"
                    disabled={isConfirming || state.phase === 'confirmed'}
                    testID="verification.confirm-button"
                  />
                  {state.phase === 'confirmed' && (
                    // The `confirmed` phase also covers 'review' /
                    // 'in_progress', where the document is stored but not
                    // yet approved — same split license.tsx makes.
                    <ApprovedBadge
                      desc={
                        state.canonicalStatus === 'approved'
                          ? t('verification.result_approved_desc')
                          : t('verification.identity_in_review_notice')
                      }
                    />
                  )}
                </>
              )}

              {/* Fase 0 (contención): la respuesta se perdió pero la evidencia
                  SÍ está guardada. La persona retoma con las MISMAS fotos, o
                  elige explícitamente recapturar. Nunca un reintento ciego. */}
              {(state.phase === 'resume' || (state.phase === 'error' && state.canResume)) && (
                <ResumeState
                  desc={state.error ?? undefined}
                  onResume={() => {
                    void resume()
                  }}
                  onRecapture={reset}
                />
              )}

              {/* Declined — specific recovery path */}
              {state.phase === 'declined' && (
                <DeclinedState
                  title={t('verification.result_declined_title')}
                  desc={state.error ?? t('verification.result_declined_desc')}
                  onRetry={reset}
                />
              )}

              {/* Generic error (network, auth, scanner unavailable).
                  NOT gated on the document type any more. It used to render only when the
                  document was NOT a dui — which is the default — so a DUI capture that failed
                  set phase 'error' and then drew absolutely nothing: the person was left under
                  a checklist saying "Falta la foto del frente" with a button that did nothing
                  when tapped. An error state with no error on screen is the same dead end as
                  swallowing the error in the first place. */}
              {state.phase === 'error' && !state.canResume && (
                <DeclinedState
                  title={t('verification.error_failed')}
                  desc={state.error ?? ''}
                  onRetry={reset}
                  onOpenSettings={
                    state.errorNeedsSettings ? () => void Linking.openSettings() : undefined
                  }
                />
              )}
            </>
          )}
        </View>
      </KeyboardAwareView>
    </ScreenContainer>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────

interface CapturePromptProps {
  title: string
  cta: string
  onPress: () => void
  testID?: string
  icon?: 'camera' | 'upload'
}

/**
 * The capture frame is a guide, so it has to be the shape of the page it
 * frames. ID-3 passports (125 × 88 mm) and credit-card documents like the
 * DUI are both ~1.4–1.6:1 landscape, so a single frame serves them; on
 * `paddingVertical` alone it was a 342 × 139pt letterbox that matched neither
 * the document nor the ID-card frame the licence step of the same flow draws.
 */
const PASSPORT_PAGE_ASPECT = 1.42

function CapturePrompt({ title, cta, onPress, testID, icon = 'camera' }: CapturePromptProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const Icon = icon === 'upload' ? Upload : Camera
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md }}
    >
      {/* Left, like the intro copy above it and the frame below it — the rule
          license.tsx settled on for the same instruction. Centred, this line
          made a third alignment out of three stacked elements. */}
      <Text
        style={{
          fontSize: 16,
          lineHeight: 22,
          fontFamily: FONTS.body,
          color: colors.textPrimary,
        }}
      >
        {title}
      </Text>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={cta}
        testID={testID}
        style={{
          // `surfaceSubtle` in dark, not the violet `surface` card: a solid
          // card fill read as a slot that already holds something rather than
          // an empty one. Same choice license.tsx makes.
          backgroundColor: isDark ? colors.surfaceSubtle : colors.surface,
          borderWidth: 2,
          borderStyle: 'dashed',
          // Same token as the label and icon inside the frame. The raw brand
          // purple never adapted — it stayed #480C82 in dark, ~1.5:1 against
          // the page, while its own caption glowed lavender inside it.
          borderColor: colors.textBrand,
          borderRadius: RADIUS.lg,
          width: '100%',
          aspectRatio: PASSPORT_PAGE_ASPECT,
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.sm,
        }}
      >
        <Icon size={48} color={colors.textBrand} strokeWidth={1.5} />
        <Text
          style={{
            fontSize: 16,
            fontFamily: FONTS.bodyBold,
            color: colors.textBrand,
          }}
        >
          {cta}
        </Text>
      </Pressable>
    </Animated.View>
  )
}

interface CapturedPhotoRowProps {
  uri: string
  label: string
  onRetake: () => void
}

function CapturedPhotoRow({ uri, label, onRetake }: CapturedPhotoRowProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const { t } = useTranslation()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: colors.surface,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
        ...WEB_SHADOWS.sm,
      }}
    >
      <ExpoImage
        source={{ uri }}
        style={{ width: 56, height: 40, borderRadius: RADIUS.sm }}
        contentFit="cover"
      />
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <CheckCircle size={16} color={colors.success} />
        <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textPrimary }}>
          {label}
        </Text>
      </View>
      <Pressable
        onPress={onRetake}
        accessibilityRole="button"
        accessibilityLabel={t('verification.retake_photo')}
        hitSlop={8}
        style={{ padding: SPACING.sm }}
        testID="verification.captured-retake"
      >
        <RotateCcw size={18} color={colors.textSecondary} />
      </Pressable>
    </Animated.View>
  )
}

interface ThumbPreviewProps {
  uri: string
  /**
   * Omit when the thumbnail is not retakeable — e.g. mid-lookup, where the
   * documents are already uploaded. Wiring a no-op into the Pressable made a
   * button that announced "Volver a tomar" to a screen reader and then did
   * nothing; the non-interactive case now renders as a plain image.
   */
  onRetake?: () => void
}

const THUMB_STYLE = { width: 80, height: 56, borderRadius: RADIUS.sm, overflow: 'hidden' } as const

function ThumbPreview({ uri, onRetake }: ThumbPreviewProps) {
  const { t } = useTranslation()

  if (!onRetake) {
    return (
      <View style={THUMB_STYLE} testID="verification.thumb-preview">
        <ExpoImage source={{ uri }} style={{ width: '100%', height: '100%' }} contentFit="cover" />
      </View>
    )
  }

  return (
    <Pressable
      onPress={onRetake}
      accessibilityRole="button"
      accessibilityLabel={t('verification.retake_photo')}
      hitSlop={4}
      style={THUMB_STYLE}
      testID="verification.thumb-preview"
    >
      <ExpoImage source={{ uri }} style={{ width: '100%', height: '100%' }} contentFit="cover" />
    </Pressable>
  )
}

interface VerifyingStateProps {
  frontUri: string | null
  backUri: string | null
  phase: 'uploading' | 'verifying'
  documentType: IdDocumentType
  label: string
}

function VerifyingState({ frontUri, backUri, phase, documentType, label }: VerifyingStateProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const progress = phase === 'uploading' ? 0.28 : Math.min(0.92, 0.38 + elapsedSeconds * 0.018)
  const detail =
    phase === 'uploading'
      ? t('verification.uploading_securely')
      : documentType === 'dui'
        ? elapsedSeconds < 5
          ? t('verification.verifik_reading_dui')
          : elapsedSeconds < 18
            ? t('verification.verifik_checking_government')
            : elapsedSeconds < 32
              ? t('verification.verifik_almost_done')
              : t('verification.verifik_longer_than_usual')
        : t('verification.verifying_generic')

  useEffect(() => {
    setElapsedSeconds(0)
    impact(ImpactStyle.Light)
    const timer = setInterval(() => {
      setElapsedSeconds((value) => value + 1)
    }, 1000)
    const hapticTimer = setInterval(() => {
      impact(ImpactStyle.Light)
    }, 3200)
    return () => {
      clearInterval(timer)
      clearInterval(hapticTimer)
    }
  }, [phase])

  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{
        gap: SPACING.md,
        alignItems: 'center',
        paddingVertical: SPACING.xl,
        paddingHorizontal: SPACING.md,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
      }}
    >
      <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
        {frontUri ? <ThumbPreview uri={frontUri} /> : null}
        {backUri ? <ThumbPreview uri={backUri} /> : null}
      </View>
      {/* The whole pill/bar group runs on `textBrand` rather than the raw
          brand purple. On the dark violet card #480C82 is ~1.2:1, so the
          progress bar, its track and the spinner all disappeared into the
          surface while the caption beside them was already lavender — the
          same split the dashed capture frame had. */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm,
          backgroundColor: `${colors.textBrand}14`,
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.sm,
          borderRadius: RADIUS.full,
        }}
      >
        <ActivityIndicator size="small" color={colors.textBrand} />
        <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textBrand }}>
          {label}
        </Text>
      </View>
      <View
        style={{
          width: '100%',
          height: 8,
          borderRadius: RADIUS.full,
          backgroundColor: `${colors.textBrand}18`,
          overflow: 'hidden',
        }}
      >
        <View
          style={{
            width: `${Math.round(progress * 100)}%`,
            height: '100%',
            borderRadius: RADIUS.full,
            backgroundColor: colors.textBrand,
          }}
        />
      </View>
      <Text
        style={{
          fontSize: 13,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
          lineHeight: 18,
        }}
      >
        {detail}
      </Text>
    </Animated.View>
  )
}

function ApprovedBadge({ desc }: { desc: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      exiting={reducedMotion ? undefined : FadeOut.duration(DURATION.fast)}
      collapsable={false}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.md,
        backgroundColor: `${colors.success}14`,
        padding: SPACING.md,
        borderRadius: RADIUS.lg,
      }}
    >
      <CheckCircle size={24} color={colors.success} />
      <Text style={{ flex: 1, fontSize: 14, fontFamily: FONTS.body, color: colors.success }}>
        {desc}
      </Text>
    </Animated.View>
  )
}

interface DeclinedStateProps {
  title: string
  desc: string
  onRetry: () => void
  /**
   * Solo cuando el fallo es un permiso de cámara bloqueado: reintentar no
   * cambia la decisión del sistema, abrir Ajustes sí.
   */
  onOpenSettings?: () => void
}

interface ResumeStateProps {
  /** Optional detail: the server message or the timeout explanation. */
  desc?: string
  onResume: () => void
  onRecapture: () => void
}

/**
 * Fase 0 (contención): la respuesta se perdió, la evidencia NO.
 *
 * El servidor pudo haber terminado o no, pero las fotos están guardadas en la
 * fila. "Continuar" re-usa esa evidencia con la MISMA fila; "Tomar fotos
 * nuevas" es una recaptura explícita, nunca un fallback automático.
 */
function ResumeState({ desc, onResume, onRecapture }: ResumeStateProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: SPACING.md,
          backgroundColor: `${colors.info}14`,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
        }}
      >
        <Clock size={22} color={colors.info} />
        <View style={{ flex: 1, gap: SPACING.xs }}>
          <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('verification.resume_title')}
          </Text>
          <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
            {desc ?? t('verification.resume_desc')}
          </Text>
        </View>
      </View>
      <Button
        title={t('verification.resume_continue')}
        onPress={onResume}
        variant="primary"
        size="lg"
        testID="verification.resume-continue"
      />
      <Button
        title={t('verification.resume_recapture')}
        onPress={onRecapture}
        variant="secondary"
        size="lg"
        testID="verification.resume-recapture"
      />
    </Animated.View>
  )
}

function DeclinedState({ title, desc, onRetry, onOpenSettings }: DeclinedStateProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()
  return (
    <Animated.View
      entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{ gap: SPACING.md }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: SPACING.md,
          backgroundColor: `${colors.error}14`,
          padding: SPACING.md,
          borderRadius: RADIUS.lg,
        }}
      >
        <AlertCircle size={22} color={colors.textError} />
        <View style={{ flex: 1, gap: SPACING.xs }}>
          <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textError }}>
            {title}
          </Text>
          {desc ? (
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {desc}
            </Text>
          ) : null}
        </View>
      </View>
      <Button
        title={t('verification.try_again')}
        onPress={onRetry}
        variant="primary"
        size="lg"
        testID="verification.retry-button"
      />
      {onOpenSettings ? (
        <Button
          title={t('verification.open_settings')}
          onPress={onOpenSettings}
          variant="secondary"
          size="lg"
          testID="verification.open-settings-button"
        />
      ) : null}
    </Animated.View>
  )
}
